/**
 * The engine log, the source of truth behind the boot screen and the Output Log panel.
 *
 * Verbosity levels follow Unreal's `ELogVerbosity`. Entries are stamped with
 * seconds since engine start (`[0.047]`), keyed by a category string such as
 * `AssetRegistry` or `Cmd`.
 *
 * Storage is a bounded ring (the newest `capacity` entries). Per-verbosity
 * counts cover the whole session, so "3 warnings" stays correct after old
 * entries scroll out of the ring.
 */
import type { ChangeScheduler } from "./ChangeScheduler";
import type { Clock } from "./Clock";
import type { Unsubscribe } from "./Emitter";
import { VersionedStore } from "./store";
import type { ReadableStore } from "./store";

export type Verbosity = "Fatal" | "Error" | "Warning" | "Display" | "Log" | "Verbose";

export interface LogEntry {
  /** Unique, increasing id. Stable React key for Output Log rows. */
  readonly seq: number;
  /** Seconds since engine start. */
  readonly time: number;
  readonly category: string;
  readonly verbosity: Verbosity;
  readonly message: string;
}

export interface LogSnapshot {
  readonly entries: readonly LogEntry[];
  readonly counts: Readonly<Record<Verbosity, number>>;
}

export interface LogOptions {
  clock: Clock;
  scheduler: ChangeScheduler;
  /** Maximum retained entries. Defaults to 2000. */
  capacity?: number;
}

const EMPTY_COUNTS: Record<Verbosity, number> = {
  Fatal: 0,
  Error: 0,
  Warning: 0,
  Display: 0,
  Log: 0,
  Verbose: 0,
};

export class Log implements ReadableStore<LogSnapshot> {
  private readonly clock: Clock;
  private readonly capacity: number;
  private readonly store: VersionedStore<LogSnapshot>;
  private nextSeq = 1;

  constructor(options: LogOptions) {
    this.clock = options.clock;
    this.capacity = options.capacity ?? 2000;
    this.store = new VersionedStore<LogSnapshot>(options.scheduler, {
      entries: [],
      counts: EMPTY_COUNTS,
    });
  }

  readonly subscribe = (listener: () => void): Unsubscribe => this.store.subscribe(listener);
  readonly getSnapshot = (): LogSnapshot => this.store.getSnapshot();

  write(category: string, verbosity: Verbosity, message: string): LogEntry {
    const entry: LogEntry = {
      seq: this.nextSeq++,
      time: roundToMillis(this.clock.sinceStart()),
      category,
      verbosity,
      message,
    };

    const previous = this.store.getSnapshot();
    this.store.publish({
      entries: appendBounded(previous.entries, entry, this.capacity),
      counts: { ...previous.counts, [verbosity]: previous.counts[verbosity] + 1 },
    });

    return entry;
  }

  error(category: string, message: string): LogEntry {
    return this.write(category, "Error", message);
  }

  warning(category: string, message: string): LogEntry {
    return this.write(category, "Warning", message);
  }

  display(category: string, message: string): LogEntry {
    return this.write(category, "Display", message);
  }

  log(category: string, message: string): LogEntry {
    return this.write(category, "Log", message);
  }

  verbose(category: string, message: string): LogEntry {
    return this.write(category, "Verbose", message);
  }
}

/** Returns a new array with `item` appended, dropping the oldest items beyond `capacity`. */
function appendBounded<T>(items: readonly T[], item: T, capacity: number): readonly T[] {
  const start = Math.max(0, items.length + 1 - capacity);
  return [...items.slice(start), item];
}

/** Keeps timestamps at millisecond precision, matching the `[0.047]` display format. */
function roundToMillis(seconds: number): number {
  return Math.round(seconds * 1000) / 1000;
}
